import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import { CardAvatar as GenericCardAvatar } from '@sorare/core/src/components/card/Avatar';
import { useIntlContext } from '@sorare/core/src/contexts/intl';

import { LazyAllLastOwnedCardsBySo5Competition } from '../LazyAllLastOwnedCardsBySo5Competition';
import type {
  LastOwnedCardsBySo5Competition_cards,
  LastOwnedCardsBySo5Competition_so5Competition,
} from './__generated__/index.graphql';

const LastOwnedCardsRoot = styled(Horizontal).attrs({
  gap: 0.5,
})`
  padding: 2px;
  background: var(--surface-primary-nd-50, rgba(255, 255, 255, 0.07));
  border-radius: var(--intermediate-unit);
`;

const squareCss = css`
  width: 20px;
  height: 20px;
  margin-left: -10px;
`;

const CardAvatar = styled(GenericCardAvatar)`
  ${squareCss}
  &:first-child {
    margin-left: 0;
  }
`;

type Props = {
  so5Competition: LastOwnedCardsBySo5Competition_so5Competition;
};

const LastOwnedCardsByEligibility = ({
  cards,
  slug,
  displayName,
  seasonality,
  seasonalityName,
}: {
  cards: LastOwnedCardsBySo5Competition_cards;
  slug: string;
  displayName: string;
  seasonality: So5LeaderboardSeasonality;
  seasonalityName: string;
}) => {
  const { nodes, totalCount } = cards;
  const { formatNumber } = useIntlContext();

  if (!cards.nodes.length) return null;

  return (
    <Tooltip
      title={
        <LazyAllLastOwnedCardsBySo5Competition
          so5CompetitionSlug={slug}
          displayName={displayName}
          count={cards.totalCount}
          seasonality={seasonality}
          seasonalityName={seasonalityName}
        />
      }
    >
      <LastOwnedCardsRoot>
        <Horizontal gap={0}>
          {nodes.map(card => {
            return (
              <CardAvatar key={card.slug} card={card} maxWidth={40} noTitle />
            );
          })}
        </Horizontal>

        <LabelS
          bold
          color="var(--c-nd-600)"
          className={classNames({
            'text-gradient-color':
              seasonality === So5LeaderboardSeasonality.IN_SEASON,
          })}
          style={
            {
              '--text-gradient-color':
                'var(--c-gradient-conversionCreditBorder)',
            } as CSSProperties
          }
        >
          x{formatNumber(totalCount)}
        </LabelS>
        <SportSeasonalityIcon size={1.5} seasonality={seasonality} />
      </LastOwnedCardsRoot>
    </Tooltip>
  );
};

export const LastOwnedCardsBySo5Competition = ({ so5Competition }: Props) => {
  const {
    inSeasonCards,
    allCards,
    slug,
    displayName,
    inSeasonName,
    allSeasonsName,
  } = so5Competition;

  return (
    <Horizontal>
      <LastOwnedCardsByEligibility
        cards={inSeasonCards}
        slug={slug}
        displayName={displayName}
        seasonality={So5LeaderboardSeasonality.IN_SEASON}
        seasonalityName={inSeasonName}
      />
      <LastOwnedCardsByEligibility
        cards={allCards}
        slug={slug}
        displayName={displayName}
        seasonality={So5LeaderboardSeasonality.ALL_SEASONS}
        seasonalityName={allSeasonsName}
      />
    </Horizontal>
  );
};

const LastOwnedCardsBySo5CompetitionFragment = gql`
  fragment LastOwnedCardsBySo5Competition_cards on AnyCardInterfaceConnection {
    totalCount
    nodes {
      slug
      rarityTyped
      ...CardAvatar_anyCard
    }
  }
` as TypedDocumentNode<LastOwnedCardsBySo5Competition_cards>;

LastOwnedCardsBySo5Competition.fragments = {
  so5Competition: gql`
    fragment LastOwnedCardsBySo5Competition_so5Competition on So5Competition {
      slug
      displayName
      inSeasonCards: cards(
        first: 3
        ownedByMe: true
        inSeasonEligible: true
        sorts: [RECENTLY_OWNED_FIRST]
      ) {
        ...LastOwnedCardsBySo5Competition_cards
      }
      allCards: cards(
        first: 3
        ownedByMe: true
        classicOnly: true
        sorts: [RECENTLY_OWNED_FIRST]
      ) {
        ...LastOwnedCardsBySo5Competition_cards
      }
      inSeasonName: seasonalityName(seasonality: IN_SEASON)
      allSeasonsName: seasonalityName(seasonality: ALL_SEASONS)
    }
    ${LastOwnedCardsBySo5CompetitionFragment}
    ${CardAvatar.fragments.anyCard}
  ` as TypedDocumentNode<LastOwnedCardsBySo5Competition_so5Competition>,
};
